import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import classes from "./SupplierModal.module.css";
import api, { withCompanyId } from "../../Components/API/Api";
import { useNavigate } from "react-router-dom";

export default function SupplierModal({ show, onHide, editMode = false, supplierData = null, onSupplierUpdated = null }) {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [taxStations, setTaxStations] = useState([]);
  
  // CAC verification states
  const [cacVerification, setCacVerification] = useState({
    isVerifying: false,
    isVerified: false,
    message: "",
    companyData: null
  });
  // STIN verification states
  const [stinVerification, setStinVerification] = useState({
    isVerifying: false,
    isVerified: false,
    message: ""
  });

  // Backend may return `{ error: { tin: ["..."] } }` for STIN validation.
  // React can't render objects, so normalize to a readable string.
  const formatTinMessage = (msg) => {
    if (msg === null || msg === undefined) return "";
    if (typeof msg === "string" || typeof msg === "number" || typeof msg === "boolean") {
      return String(msg);
    }
    if (typeof msg === "object") {
      const tinArr = msg?.error?.tin ?? msg?.tin;
      if (Array.isArray(tinArr) && tinArr.length > 0) return String(tinArr[0]);
      const err = msg?.error;
      if (typeof err === "string") return err;
      return JSON.stringify(msg);
    }
    return String(msg);
  };
  // Create TIN loading
  const [creatingTin, setCreatingTin] = useState(false);
  // Inline status for create TIN
  const [createTinStatus, setCreateTinStatus] = useState({ status: 'idle', message: '' });
  // Track whether email was auto-filled from CAC response
  const [emailAutoFilled, setEmailAutoFilled] = useState(false);
  // Opens RC/BN selector when user focuses/clicks the CAC input
  const [cacTypeOpen, setCacTypeOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_number: "",
    address: "",
    account_number: "",
    bank_name: "",
    account_name: "",
    wht: "",
    stin: "",
    cac: "",
    // Backend expects a parameter named `type` to indicate RC vs BN
    type: "RC",
  });

  // ✅ Handle redirect if no token
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("Authorization token not found.");
      navigate("/signin");
    }
  }, [navigate]);

  // Reset form when modal opens/closes or when edit mode changes
  useEffect(() => {
    if (show) {
      if (editMode && supplierData) {
        // Load supplier data for editing
        setFormData({
          name: supplierData.name || "",
          email: supplierData.email || "",
          phone_number: supplierData.phone_number || "",
          address: supplierData.address || "",
          account_number: supplierData.account_number || "",
          bank_name: supplierData.bank_name || "",
          account_name: supplierData.account_name || "",
          wht: supplierData.wht || "",
          stin: supplierData.stin || "",
          cac: supplierData.cac || "",
          type: supplierData.type || "RC",
          id: supplierData.id, // Store ID for update
        });
        setCacVerification({
          isVerifying: false,
          isVerified: false,
          message: "",
          companyData: null
        });
        setStinVerification({
          isVerifying: false,
          isVerified: false,
          message: ""
        });
        setEmailAutoFilled(false);
        setCreateTinStatus({ status: 'idle', message: '' });
      } else {
        // Reset form for creating new supplier
        setFormData({
          name: "",
          email: "",
          phone_number: "",
          address: "",
          account_number: "",
          bank_name: "",
          account_name: "",
          wht: "",
          stin: "",
          cac: "",
          type: "RC",
        });
        setCacVerification({
          isVerifying: false,
          isVerified: false,
          message: "",
          companyData: null
        });
        setStinVerification({
          isVerifying: false,
          isVerified: false,
          message: ""
        });
        setEmailAutoFilled(false);
        setCreateTinStatus({ status: 'idle', message: '' });
      }
    }
  }, [show, editMode, supplierData]);

  // CAC verification function
  const verifyCacNumber = async (cacNumber, cacType) => {
    if (!cacNumber || cacNumber.trim().length < 3) {
      // setCacVerification({
      //   isVerifying: false,
      //   isVerified: false,
      //   message: "",
      //   companyData: null
      // });
      setEmailAutoFilled(false);
      return;
    }

    // setCacVerification(prev => ({
    //   ...prev,
    //   isVerifying: true,
    //   message: "Verifying CAC number..."
    // }));

      try {
      const response = await api.get
      // ('/v1/beneficiary/verify-cac', {
      //   params: { cac_number: cacNumber, type: cacType },
      // });

      if (response.status === 200 && (response.data.success || response.data.status === 'success' || response.data.message)) {
        const companyData = response.data.data || response.data;
        console.log("CAC verification response:", response.data);
        console.log("Company data:", companyData);
        
        setCacVerification({
          isVerifying: false,
          isVerified: true,
          message: "CAC number verified successfully!",
          companyData
        });

        // Auto-fill the form with company data - try multiple possible field names
        const derivedName = companyData.companyName || companyData.name || companyData.company_name;
        const derivedEmail = companyData.companyEmail || companyData.email || companyData.company_email || companyData.email_address || companyData.contactEmail;
        // Mark whether email originated from CAC response so we only lock when auto-filled
        setEmailAutoFilled(Boolean(derivedEmail));
        // Address comes from "address" as per latest mapping
        const derivedAddress = companyData.address;

        setFormData(prev => ({
          ...prev,
          name: derivedName ? derivedName : prev.name,
          email: derivedEmail ? derivedEmail : prev.email,
          address: derivedAddress ? derivedAddress : prev.address,
        }));
      } else {
        setCacVerification({
          isVerifying: false,
          isVerified: false,
          message: response.data.message || "Invalid CAC number",
          companyData: null
        });
      }
    } catch (error) {
      // setCacVerification({
      //   isVerifying: false,
      //   isVerified: false,
      //   message: error.response?.data?.message || "Failed to verify CAC number",
      //   companyData: null
      // });
    }
  };

  // STIN verification function
  const verifyTin = async () => {
    const cacNumber = formData.cac;
    const phone = formData.phone_number;
    const email = formData.email;
    const tin = formData.stin;

    // Require all params before verifying
    if (!cacNumber || !phone || !email || !tin) {
      setStinVerification(prev => ({
        ...prev,
        isVerifying: false,
        isVerified: false,
        message: ""
      }));
      return;
    }

    setStinVerification({ isVerifying: true, isVerified: false, message: "Verifying STIN..." });

      try {
      const response = await api.get('/v1/beneficiary/verify-tin', { params: { phone, email, tin, cac_number: cacNumber } });

      if (response.status === 200 && (response.data.success || response.data.status === 'success' || response.data.message)) {
        setStinVerification({
          isVerifying: false,
          isVerified: true,
          message: response.data.message || "STIN verified successfully!"
        });
      } else {
        setEmailAutoFilled(false);
        setStinVerification({
          isVerifying: false,
          isVerified: false,
          message: response.data.message || "Invalid STIN"
        });
      }
    } catch (error) {
        setEmailAutoFilled(false);
      setStinVerification({
        isVerifying: false,
        isVerified: false,
        message: error.response?.data?.message || "Failed to verify STIN"
      });
    }
  };

  // Create TIN function
  const createTin = async () => {
    if (creatingTin) return;

    // Ensure required fields are present
    const { name, email, phone_number, cac } = formData;
    if (!name || !email || !phone_number || !cac) {
      setCreateTinStatus({ status: 'error', message: 'Company name, email, phone, and CAC are required to create STIN.' });
      return;
    }

      try {
      setCreatingTin(true);
      setCreateTinStatus({ status: 'loading', message: 'Creating STIN…' });
      const response = await api.get('/v1/beneficiary/create-tin', { params: { company_name: name, name, email, phone: phone_number, cac, type: formData.type } });

      if (response.status === 200 || response.status === 201) {
        const createdTin =
          (typeof response.data?.data === 'string' || typeof response.data?.data === 'number')
            ? String(response.data.data)
            : (response.data?.data?.tin || response.data?.tin);
        if (createdTin) {
          setFormData(prev => ({ ...prev, stin: String(createdTin) }));
        }
        setCreateTinStatus({ status: 'success', message: response.data?.message || 'STIN created successfully!' });
      } else {
        throw new Error(response.data?.message || "Failed to create STIN");
      }
    } catch (error) {
      setCreateTinStatus({ status: 'error', message: error.response?.data?.message || error.message || 'Failed to create STIN' });
    } finally {
      setCreatingTin(false);
    }
  };

  // Debounced CAC verification
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.cac) {
        verifyCacNumber(formData.cac, formData.type);
      }
    }, 1000); // 1 second delay

    return () => clearTimeout(timeoutId);
  }, [formData.cac, formData.type]);

  // Debug: Monitor form data changes
  useEffect(() => {
    console.log("Form data updated:", formData);
  }, [formData]);

  // Debounced STIN verification (requires cac, phone, email, and stin)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.stin) {
        verifyTin();
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [formData.stin, formData.cac, formData.phone_number, formData.email]);

  // ✅ Fetch states
  // ✅ Fetch states
  // Fetch states
  const stateOfWht = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await api.get('/v1/fetch-all-state');

      // Your states are directly inside `response.data.data`
      setTaxStations(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch tax stations:", error);
      Swal.fire("Error", "Could not load tax stations", "error");
    }
  };

  // ✅ Call it when modal opens
  useEffect(() => {
    if (show) {
      stateOfWht();
    }
  }, [show]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'email') setEmailAutoFilled(false);
    setFormData((prev) => ({ ...prev, [name]: String(value) }));
  };

  // Selected WHT state helpers
  const selectedWht = taxStations.find((s) => String(s.id_no) === String(formData.wht));
  const isOgunStateSelected = (selectedWht?.state || "").toLowerCase() === "ogun state";
 
  // Overall form validity: 
  // In edit mode, skip verification requirements since supplier is already verified
  // In create mode, require all verifications
  const isFormValid = Boolean(
    formData.name &&
    formData.email &&
    formData.address &&
    formData.phone_number &&
    formData.wht &&
    // In create mode, require STIN verification for Ogun State
    // In edit mode, skip this requirement since supplier is already verified
    (!isOgunStateSelected || editMode || (formData.stin && stinVerification.isVerified))
  );

  // Handle supplier form submit (both create and update)
  const handleCreateSupplier = async () => {
    if (!isFormValid) {
      Swal.fire("Incomplete form", "Please complete all required fields and verifications before submitting.", "warning");
      return;
    }
    setCreating(true);

    try {
      let res;
      if (editMode && formData.id) {
        // Update existing supplier - use POST instead of PUT
        res = await api.post(`/v1/beneficiary/update`, withCompanyId(formData));
      } else {
        // Create new supplier
        res = await api.post(`/v1/beneficiary/add`, withCompanyId(formData));
      }

      if (res.status === 200 || res.status === 201) {
        setCreating(false);
        const alertOptions = {
          title: "Success",
          text: editMode ? "Supplier updated successfully!" : "Supplier created successfully!",
          icon: "success",
          customClass: {
            popup: "swal-popup-zindex",
          },
        };

        // ✅ Close modal and wait for user to click OK before refreshing
        onHide();
        setTimeout(() => {
          Swal.fire(alertOptions).then(() => {
            if (editMode && onSupplierUpdated) {
              // Call callback to refresh suppliers list without full page reload
              onSupplierUpdated();
            } else {
              window.location.reload();
            }
          });
        }, 300);
      } else {
        throw new Error(res.data?.message || editMode ? "Failed to update supplier" : "Failed to add supplier");
      }
    } catch (error) {
      // Handle validation errors from backend
      const errorData = error.response?.data;
      let errorMessage = error.message || "Something went wrong";

      // Check if the error is a phone number validation error
      if (errorData?.message?.error?.phone_number) {
        const phoneErrors = errorData.message.error.phone_number;
        errorMessage = Array.isArray(phoneErrors) ? phoneErrors[0] : phoneErrors;
        
        // Show error without closing modal so user can fix phone number
        Swal.fire({
          title: "Phone Number Error",
          text: errorMessage,
          icon: "error",
          customClass: {
            popup: "swal-popup-zindex",
          },
        });
      } else if (errorData?.message?.error) {
        // Handle other validation errors
        const errorObj = errorData.message.error;
        const firstErrorKey = Object.keys(errorObj)[0];
        const firstErrorValue = errorObj[firstErrorKey];
        errorMessage = Array.isArray(firstErrorValue) ? firstErrorValue[0] : firstErrorValue;
        
        Swal.fire({
          title: "Validation Error",
          text: errorMessage,
          icon: "error",
          customClass: {
            popup: "swal-popup-zindex",
          },
        });
      } else {
        // Generic error
        Swal.fire({
          title: "Error",
          text: errorMessage || (editMode ? "Failed to update supplier" : "Failed to create supplier"),
          icon: "error",
          customClass: {
            popup: "swal-popup-zindex",
          },
        });
      }
      setCreating(false);
    }
  };

  if (!show) return null;

  return (
    <div className={classes.customModal}>
      <div className={classes.modalDialog}>
        <div className={classes.modalHeader}>
          <span className={classes.modalTitle}>
            {editMode ? "Edit Supplier/Beneficiary" : "Create Supplier/Beneficiary"}
          </span>
          <button className={classes.closeButton} onClick={onHide}>
            &times;
          </button>
        </div>

        <div className={classes.modalBody}>
          <form onSubmit={(e) => e.preventDefault()}>
            <div className={classes.formRow}>
              <div className={classes.col6}>
                <div style={{ position: "relative", display: "flex", flexDirection: "column" }}>
                  <label style={{ display: "block", marginBottom: 6 }}>CAC</label>
                  <input
                    type="text"
                    name="cac"
                    placeholder={
                      formData.type === "BN"
                        ? "Enter BN number"
                        : "Enter RC number"
                    }
                    value={formData.cac}
                    onChange={handleChange}
                    onFocus={() => setCacTypeOpen(true)}
                    onClick={() => setCacTypeOpen(true)}
                    onBlur={() => {
                      // Let option click register first.
                      setTimeout(() => setCacTypeOpen(false), 150);
                    }}
                  />

                  {cacTypeOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        width: "100%",
                        background: "#fff",
                        border: "1px solid rgba(0,0,0,0.15)",
                        borderRadius: 8,
                        padding: 8,
                        marginTop: 6,
                        zIndex: 50,
                        boxShadow: "0 10px 20px rgba(0,0,0,0.08)",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
                        Select type
                      </div>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, type: "RC" }));
                          setCacTypeOpen(false);
                        }}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "6px 8px",
                          borderRadius: 6,
                          border: "1px solid rgba(0,0,0,0.08)",
                          background: formData.type === "RC" ? "#FEF9C3" : "#fff",
                          cursor: "pointer",
                          marginBottom: 6,
                        }}
                      >
                        RC
                      </button>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, type: "BN" }));
                          setCacTypeOpen(false);
                        }}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "6px 8px",
                          borderRadius: 6,
                          border: "1px solid rgba(0,0,0,0.08)",
                          background: formData.type === "BN" ? "#FEF9C3" : "#fff",
                          cursor: "pointer",
                        }}
                      >
                        BN
                      </button>
                    </div>
                  )}
                </div>
                {/* CAC verification status */}
                {cacVerification.isVerifying && (
                  <div className={classes.verificationStatus}>
                    <span style={{ color: '#007bff' }}>⏳ {cacVerification.message}</span>
                  </div>
                )}
                {cacVerification.isVerified && !cacVerification.isVerifying && (
                  <div className={classes.verificationStatus}>
                    <span style={{ color: '#28a745' }}>✅ {cacVerification.message}</span>
                  </div>
                )}
                {!cacVerification.isVerified && !cacVerification.isVerifying && cacVerification.message && (
                  <div className={classes.verificationStatus}>
                    <span style={{ color: '#dc3545' }}>❌ {cacVerification.message}</span>
                  </div>
                )}
              </div>
              <div className={classes.col6}>
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter supplier name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={cacVerification.isVerified}
                />
              </div>
              <div className={classes.col6}>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleChange}
                  // Disabled only when CAC auto-filled the email
                  disabled={cacVerification.isVerified && emailAutoFilled}
                />
              </div>

              <div className={classes.col6}>
                <label>Phone Number</label>
                <input
                  type="text"
                  name="phone_number"
                  placeholder="Enter phone number"
                  value={formData.phone_number}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={classes.formRow}>
              <div className={classes.col6}>
                <label>State of WHT</label>
                <select
                type="text"
                  name="wht"
                  value={formData.wht}
                  onChange={handleChange}
                  className={classes.sel_pen}
                >
                  <option value="">Select State</option>
                  {taxStations.map((state) => (
                    <option key={state.id_no} value={state.id_no}>
                      {state.state}
                    </option>
                  ))}
                </select>



              </div>

              <div className={classes.col6}>
                <label>STIN</label>
                <input
                type='text'
                  name="stin"
                  value={formData.stin}
                  onChange={handleChange}
                  className={classes.sel_pen1}
                  disabled={!formData.wht}
                />
                {/* STIN verification status */}
                {stinVerification.isVerifying && (
                  <div className={classes.verificationStatus}>
                    <span style={{ color: '#007bff' }}>⏳ {formatTinMessage(stinVerification.message)}</span>
                  </div>
                )}
                {stinVerification.isVerified && !stinVerification.isVerifying && (
                  <div className={classes.verificationStatus}>
                    <span style={{ color: '#28a745' }}>✅ {formatTinMessage(stinVerification.message)}</span>
                  </div>
                )}
                {!stinVerification.isVerified && !stinVerification.isVerifying && isOgunStateSelected && !formData.stin && (
                  <div className={classes.verificationStatus}>
                    {createTinStatus.status === 'idle' && (
                      <>
                        <span>Do not have an STIN? </span>
                        <button type="button" onClick={createTin} style={{ background: 'none', border: 'none', color: '#007bff', textDecoration: 'underline', cursor: 'pointer', padding: 0 }} disabled={creatingTin}>
                          {creatingTin ? 'Creating…' : 'Create one'}
                        </button>
                      </>
                    )}
                    {createTinStatus.status !== 'idle' && (
                      <span style={{ color: createTinStatus.status === 'success' ? '#28a745' : createTinStatus.status === 'error' ? '#dc3545' : '#007bff' }}>
                        {typeof createTinStatus.message === 'object' ? JSON.stringify(createTinStatus.message) : String(createTinStatus.message)}
                      </span>
                    )}
                  </div>
                )}
                {!stinVerification.isVerified && !stinVerification.isVerifying && stinVerification.message && (
                  <div className={classes.verificationStatus}>
                    <span style={{ color: '#dc3545' }}>❌ {formatTinMessage(stinVerification.message)}</span>
                  </div>
                )}
                
              </div>

              <div className={classes.col6}>
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  placeholder="Enter address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={cacVerification.isVerified}
                />
              </div>

              <div className={classes.col6}>
                <label>Account Number</label>
                <input
                  type="text"
                  name="account_number"
                  placeholder="Enter account number"
                  value={formData.account_number}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={classes.formRow}>
              <div className={classes.col6}>
                <label>Bank Name</label>
                <input
                  type="text"
                  name="bank_name"
                  placeholder="Enter bank name"
                  value={formData.bank_name}
                  onChange={handleChange}
                />
              </div>
              <div className={classes.col6}>
                <label>Account Name</label>
                <input
                  type="text"
                  name="account_name"
                  placeholder="Enter account name"
                  value={formData.account_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <button
              type="button"
              className={classes.createBtn}
              onClick={handleCreateSupplier}
              disabled={creating || !isFormValid}
            >
              {creating ? (editMode ? "Updating Supplier..." : "Creating Supplier...") : (editMode ? "Update Supplier" : "Create Supplier")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
